import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import GameRenderer from './GameRenderer';

// Score Result Modal shown after quiz completes (mirrors GameRenderer's ScoreModal)
const QuizScoreModal = ({ isCorrect, correctAnswerText, selectedAnswerText, onContinue }) => {
  const isPerfect = isCorrect;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
          isPerfect ? 'bg-yellow-100' : 'bg-red-100'
        }`}>
          <Icon
            name={isPerfect ? 'CheckCircle2' : 'XCircle'}
            size={32}
            className={isPerfect ? 'text-green-500' : 'text-red-500'}
          />
        </div>

        <h3 className="text-xl font-bold text-foreground mb-1">
          {isPerfect ? 'Correct! 🎉' : 'Not Quite Right'}
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          {isPerfect ? 'Great job! You got it right.' : 'Keep practicing — you\'ll get it next time!'}
        </p>

        {/* Score display */}
        <div className={`rounded-xl p-4 mb-4 ${
          isPerfect
            ? 'bg-green-50 border border-green-200' :'bg-red-50 border border-red-200'
        }`}>
          <div className={`text-2xl font-bold mb-1 ${
            isPerfect ? 'text-green-700' : 'text-red-700'
          }`}>
            {isPerfect ? '1/1' : '0/1'}
          </div>
          <div className={`text-base font-semibold ${
            isPerfect ? 'text-green-600' : 'text-red-600'
          }`}>
            {isPerfect ? '100%' : '0%'}
          </div>
          {!isPerfect && correctAnswerText && (
            <div className="mt-2 pt-2 border-t border-red-200">
              <p className="text-xs text-muted-foreground">Correct answer:</p>
              <p className="text-sm font-medium text-foreground mt-0.5">{correctAnswerText}</p>
            </div>
          )}
        </div>

        {/* Stars earned for this quiz */}
        <div className="flex items-center justify-center space-x-1 mb-5">
          {[1, 2, 3]?.map(s => (
            <Icon
              key={s}
              name="Star"
              size={24}
              className={s <= (isPerfect ? 3 : 1) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
            />
          ))}
        </div>

        <Button onClick={onContinue} iconName="ArrowRight" iconPosition="right" fullWidth>
          Continue
        </Button>
      </div>
    </div>
  );
};

const ContentRenderer = ({ content, textSize, onComplete, isCompleted }) => {
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showQuizResult, setShowQuizResult] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [imageZoomed, setImageZoomed] = useState(false);
  const [videoWatched, setVideoWatched] = useState(isCompleted);
  // Quiz score modal state
  const [quizScoreModal, setQuizScoreModal] = useState(null);

  const handleQuizSubmit = () => {
    if (selectedAnswer !== null) {
      setShowQuizResult(true);
      const correctAnswer = parseInt(content?.content_data?.correct_answer);
      const isCorrect = selectedAnswer === correctAnswer;
      const correctAnswerText = content?.content_data?.options?.[correctAnswer];
      const selectedAnswerText = content?.content_data?.options?.[selectedAnswer];
      if (isCorrect) {
        setQuizCompleted(true);
      }
      // Show score modal instead of auto-completing
      setQuizScoreModal({
        isCorrect,
        correctAnswerText,
        selectedAnswerText,
        scoreData: { correct: isCorrect, total: 1, score: isCorrect ? 1 : 0 }
      });
    }
  };

  const handleQuizModalContinue = () => {
    const modal = quizScoreModal;
    setQuizScoreModal(null);
    onComplete?.(modal?.scoreData);
  };

  const handleMarkAsRead = () => {
    onComplete?.();
  };

  const handleImageViewed = () => {
    onComplete?.();
  };

  const handleVideoWatched = () => {
    setVideoWatched(true);
    onComplete?.();
  };

  const renderTextContent = () => (
    <div className={`space-y-3 sm:space-y-4 text-size-${textSize}`}>
      <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-500/10 rounded-full flex items-center justify-center flex-shrink-0">
          <Icon name="FileText" size={16} className="text-blue-500 sm:w-5 sm:h-5" />
        </div>
        <h2 className="text-lg sm:text-2xl font-bold text-foreground">
          {content?.content_data?.title || 'Text Section'}
        </h2>
      </div>
      <div className="prose prose-sm sm:prose-lg max-w-none">
        <p className="text-sm sm:text-base text-foreground leading-relaxed whitespace-pre-wrap">
          {content?.content_data?.body}
        </p>
      </div>
      {!isCompleted && (
        <div className="pt-3 sm:pt-4">
          <Button onClick={handleMarkAsRead} iconName="Check" fullWidth>
            Mark as Read
          </Button>
        </div>
      )}
      {isCompleted && (
        <div className="flex items-center justify-center space-x-2 text-success pt-3 sm:pt-4">
          <Icon name="CheckCircle2" size={18} />
          <span className="text-xs sm:text-sm font-medium">Completed</span>
        </div>
      )}
    </div>
  );

  const renderImageContent = () => (
    <div className="space-y-3 sm:space-y-4">
      <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-500/10 rounded-full flex items-center justify-center flex-shrink-0">
          <Icon name="Image" size={16} className="text-purple-500 sm:w-5 sm:h-5" />
        </div>
        <h2 className="text-lg sm:text-2xl font-bold text-foreground">Image</h2>
      </div>
      <div
        className={`relative rounded-xl overflow-hidden cursor-pointer transition-all ${
          imageZoomed ? 'fixed inset-2 sm:inset-4 z-50' : ''
        }`}
        onClick={() => setImageZoomed(!imageZoomed)}
      >
        <Image
          src={content?.content_data?.url}
          alt={content?.content_data?.alt || content?.content_data?.caption || 'Trail image'}
          className="w-full h-auto"
        />
        {!imageZoomed && (
          <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-black/50 text-white px-2 sm:px-3 py-1 rounded-full text-xs flex items-center space-x-1">
            <Icon name="ZoomIn" size={12} />
            <span className="hidden sm:inline">Click to zoom</span>
            <span className="sm:hidden">Zoom</span>
          </div>
        )}
      </div>
      {content?.content_data?.caption && (
        <p className="text-xs sm:text-sm text-muted-foreground text-center italic">
          {content?.content_data?.caption}
        </p>
      )}
      {!isCompleted && (
        <Button onClick={handleImageViewed} iconName="Check" fullWidth>
          Continue
        </Button>
      )}
      {isCompleted && (
        <div className="flex items-center justify-center space-x-2 text-success">
          <Icon name="CheckCircle2" size={18} />
          <span className="text-xs sm:text-sm font-medium">Completed</span>
        </div>
      )}
    </div>
  );

  const renderVideoContent = () => {
    const rawUrl = content?.content_data?.url || '';

    // Convert YouTube watch URL to embed URL
    const getEmbedUrl = (url) => {
      if (!url) return '';
      // Already an embed URL
      if (url?.includes('youtube.com/embed/')) return url;
      // youtu.be short link
      const shortMatch = url?.match(/youtu\.be\/([^?&]+)/);
      if (shortMatch) return `https://www.youtube.com/embed/${shortMatch?.[1]}`;
      // Standard watch URL
      const watchMatch = url?.match(/[?&]v=([^?&]+)/);
      if (watchMatch) return `https://www.youtube.com/embed/${watchMatch?.[1]}`;
      // Return as-is for non-YouTube URLs
      return url;
    };

    const embedUrl = getEmbedUrl(rawUrl);

    return (
      <div className="space-y-3 sm:space-y-4">
        <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-pink-500/10 rounded-full flex items-center justify-center flex-shrink-0">
            <Icon name="Video" size={16} className="text-pink-500 sm:w-5 sm:h-5" />
          </div>
          <h2 className="text-lg sm:text-2xl font-bold text-foreground">
            {content?.content_data?.title || 'Video'}
          </h2>
        </div>
        <div className="rounded-xl overflow-hidden bg-muted w-full" style={{ aspectRatio: '16/9' }}>
          {embedUrl ? (
            <iframe
              src={embedUrl}
              title={content?.content_data?.title || 'Video'}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <p className="text-sm text-muted-foreground">No video URL provided</p>
            </div>
          )}
        </div>
        {content?.content_data?.duration && (
          <p className="text-xs sm:text-sm text-muted-foreground">
            Duration: {Math.floor(content?.content_data?.duration / 60)}:{String(content?.content_data?.duration % 60)?.padStart(2, '0')}
          </p>
        )}
        {!isCompleted && !videoWatched && (
          <Button onClick={handleVideoWatched} iconName="Check" fullWidth>
            Mark as Watched
          </Button>
        )}
        {(isCompleted || videoWatched) && (
          <div className="flex items-center justify-center space-x-2 text-success">
            <Icon name="CheckCircle2" size={18} />
            <span className="text-xs sm:text-sm font-medium">Completed</span>
          </div>
        )}
      </div>
    );
  };

  const renderQuizContent = () => {
    const correctAnswer = parseInt(content?.content_data?.correct_answer);

    return (
      <>
        {/* Quiz Score Modal */}
        {quizScoreModal && (
          <QuizScoreModal
            isCorrect={quizScoreModal?.isCorrect}
            correctAnswerText={quizScoreModal?.correctAnswerText}
            selectedAnswerText={quizScoreModal?.selectedAnswerText}
            onContinue={handleQuizModalContinue}
          />
        )}
        <div className="space-y-4 sm:space-y-6">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-500/10 rounded-full flex items-center justify-center flex-shrink-0">
              <Icon name="HelpCircle" size={16} className="text-orange-500 sm:w-5 sm:h-5" />
            </div>
            <h2 className="text-lg sm:text-2xl font-bold text-foreground">Quiz Question</h2>
          </div>

          <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 sm:p-6">
            <p className="text-base sm:text-lg font-medium text-foreground mb-4 sm:mb-6">
              {content?.content_data?.question}
            </p>

            <div className="space-y-2 sm:space-y-3">
              {content?.content_data?.options?.map((option, idx) => {
                const isSelected = selectedAnswer === idx;
                const isCorrect = idx === correctAnswer;
                const showResult = showQuizResult;

                return (
                  <button
                    key={idx}
                    onClick={() => !showQuizResult && setSelectedAnswer(idx)}
                    disabled={showQuizResult || quizCompleted}
                    className={`w-full p-3 sm:p-4 rounded-lg border-2 text-left transition-all ${
                      showResult
                        ? isCorrect
                          ? 'border-success bg-success/10'
                          : isSelected
                          ? 'border-error bg-error/10' :'border-border bg-muted/30'
                        : isSelected
                        ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50 hover:bg-muted/50'
                    } ${(showQuizResult || quizCompleted) ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 sm:space-x-3">
                        <span className="font-semibold text-xs sm:text-sm">{String.fromCharCode(65 + idx)}.</span>
                        <span className="text-xs sm:text-sm">{option}</span>
                      </div>
                      {showResult && isCorrect && (
                        <Icon name="CheckCircle2" size={18} className="text-success flex-shrink-0" />
                      )}
                      {showResult && isSelected && !isCorrect && (
                        <Icon name="XCircle" size={18} className="text-error flex-shrink-0" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {!showQuizResult && !quizCompleted && (
            <Button
              onClick={handleQuizSubmit}
              disabled={selectedAnswer === null}
              iconName="Send"
              fullWidth
            >
              Submit Answer
            </Button>
          )}

          {(quizCompleted || showQuizResult) && (
            <div className="flex items-center justify-center space-x-2 text-success">
              <Icon name="CheckCircle2" size={18} />
              <span className="text-xs sm:text-sm font-medium">Quiz Completed</span>
            </div>
          )}
        </div>
      </>
    );
  };

  const renderCompletionContent = () => (
    <div className="text-center space-y-4 sm:space-y-6">
      <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-success/10 rounded-full mb-3 sm:mb-4">
        <Icon name="Award" size={36} className="text-success" />
      </div>
      <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
        {content?.content_data?.title || 'Congratulations!'}
      </h2>
      <p className="text-base sm:text-lg text-muted-foreground whitespace-pre-wrap">
        {content?.content_data?.message || 'You have completed this learning trail!'}
      </p>
      {!isCompleted && (
        <Button onClick={onComplete} iconName="Award" size="lg">
          Complete Trail
        </Button>
      )}
      {isCompleted && (
        <div className="flex items-center justify-center space-x-2 text-success">
          <Icon name="CheckCircle2" size={22} />
          <span className="text-base sm:text-lg font-semibold">Trail Completed!</span>
        </div>
      )}
    </div>
  );

  // Main render logic
  switch (content?.content_type) {
    case 'text':
      return renderTextContent();
    case 'image':
      return renderImageContent();
    case 'video':
      return renderVideoContent();
    case 'quiz':
      return renderQuizContent();
    case 'game':
      return (
        <GameRenderer
          content={content}
          onComplete={onComplete}
          isCompleted={isCompleted}
        />
      );
    case 'completion':
      return renderCompletionContent();
    default:
      return (
        <div className="text-center py-8">
          <Icon name="AlertCircle" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Unknown content type</p>
        </div>
      );
  }
};

export default ContentRenderer;